import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import moment from "moment";
import EmailContentEditor from "./automations/EmailContentEditor";
import { sendSubmissionFeedbackApi } from "./submissionStorage";
import { buildProgressMessage } from "./CoachFeedbackCard";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary)";

const CHANNELS = {
  chat: { label: "Chat", sent: "Chat message sent", failed: "Chat message not sent" },
  email: { label: "Email", sent: "Email sent", failed: "Email not sent" },
};

const isEmptyHtml = (html) =>
  !String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const looksLikeHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

const getFirstName = (submission = {}) => {
  if (submission.firstName) return String(submission.firstName).trim();
  const name = String(submission.memberName || "").trim();
  if (!name || name === "Unknown" || name.includes("@")) return "";
  return name.split(/\s+/)[0];
};

const entryChannels = (entry = {}) => {
  const results = entry.results || {};
  const keys = new Set(Array.isArray(entry.channels) ? entry.channels : []);
  Object.entries(results).forEach(([key, outcome]) => {
    if (outcome && (outcome.sent || outcome.error)) keys.add(key);
  });
  return Array.from(keys)
    .filter((key) => CHANNELS[key])
    .map((key) => ({
      key,
      failed: results[key]?.sent === false,
      error: results[key]?.error || "",
    }));
};

const ChannelOption = ({ label, checked, disabled, hint, onChange }) => (
  <Box>
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={<Typography sx={{ fontSize: 13.5 }}>{label}</Typography>}
    />
    {hint ? (
      <Typography sx={{ fontSize: 12, color: MUTED, ml: 4, mt: -0.75 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

/**
 * Drawer: TinyMCE feedback to member via chat and/or email.
 */
export default function ClientFeedbackDrawer({
  open,
  onClose,
  submission,
  metrics,
  onSent,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const editorRef = useRef(null);
  const chatAvailable = Boolean(submission?.memberId);
  const emailAvailable = Boolean(submission?.email);
  const [message, setMessage] = useState("");
  const [chatChecked, setChatChecked] = useState(true);
  const [emailChecked, setEmailChecked] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMessage("");
    setChatChecked(true);
    setEmailChecked(true);
    setSending(false);
  }, [open, submission?.id]);

  const firstName = getFirstName(submission || {});
  const progressText = useMemo(() => buildProgressMessage(metrics), [metrics]);
  const history = useMemo(
    () =>
      [...(submission?.coachFeedback || [])].sort(
        (a, b) => new Date(b?.sentAt || 0) - new Date(a?.sentAt || 0)
      ),
    [submission?.coachFeedback]
  );

  const wantChat = chatAvailable && chatChecked;
  const wantEmail = emailAvailable && emailChecked;
  const canSend =
    !isEmptyHtml(message) && (wantChat || wantEmail) && !sending;

  const handleInsertProgress = () => {
    if (!progressText) return;
    const first = firstName || "there";
    const snippet = `Hi ${first}, ${progressText
      .charAt(0)
      .toLowerCase()}${progressText.slice(1)}`;
    const html = `<p>${snippet}</p>`;
    if (editorRef.current?.insertContent) {
      editorRef.current.insertContent(html);
      return;
    }
    setMessage((prev) => (isEmptyHtml(prev) ? html : `${prev}${html}`));
  };

  const notifyResults = (result) => {
    if (!result.results) {
      enqueueSnackbar(result.message || "Feedback sent", { variant: "success" });
      return;
    }
    [
      ["chat", wantChat],
      ["email", wantEmail],
    ].forEach(([key, requested]) => {
      if (!requested) return;
      const outcome = result.results[key];
      if (outcome?.sent) {
        enqueueSnackbar(CHANNELS[key].sent, { variant: "success" });
        return;
      }
      enqueueSnackbar(
        outcome?.error
          ? `${CHANNELS[key].failed}: ${outcome.error}`
          : CHANNELS[key].failed,
        { variant: "warning" }
      );
    });
  };

  const handleSend = async () => {
    if (!canSend || !submission?.id) return;
    setSending(true);
    const result = await sendSubmissionFeedbackApi(submission.id, {
      message: message.trim(),
      sendChat: wantChat,
      sendEmail: wantEmail,
    });
    setSending(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Couldn't send the feedback", {
        variant: "error",
      });
      return;
    }
    notifyResults(result);
    setMessage("");
    if (onSent) await onSent(result.submission);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={sending ? undefined : onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 720, md: 840 },
          borderLeft: `1px solid ${BORDER}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.25,
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>
            Feedback for client
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 13, color: MUTED }}>
            Write once — send by chat, email, or both.
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={sending} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.25, py: 2, overflowY: "auto", flex: 1 }}>
        <Typography sx={{ fontSize: 13, color: MUTED, mb: 1 }}>
          To:{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
            {submission?.memberName || firstName || "Member"}
          </Box>
          {submission?.email ? (
            <Box component="span" sx={{ color: MUTED }}>
              {" "}
              · {submission.email}
            </Box>
          ) : null}
        </Typography>

        {open ? (
          <EmailContentEditor
            value={message}
            onChange={setMessage}
            height={280}
            onEditorReady={(editor) => {
              editorRef.current = editor;
            }}
          />
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <ChannelOption
              label="Send chat message"
              checked={wantChat}
              disabled={!chatAvailable || sending}
              hint={chatAvailable ? "" : "No portal account for chat"}
              onChange={setChatChecked}
            />
            <ChannelOption
              label="Send email"
              checked={wantEmail}
              disabled={!emailAvailable || sending}
              hint={emailAvailable ? "" : "No email on file"}
              onChange={setEmailChecked}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              ml: "auto",
              justifyContent: "flex-end",
            }}
          >
            <Tooltip title={progressText ? "" : "No measurements yet"}>
              <span>
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  disabled={!progressText || sending}
                  onClick={handleInsertProgress}
                >
                  Insert progress
                </button>
              </span>
            </Tooltip>
            <button
              type="button"
              className="small-contained-button"
              disabled={!canSend}
              onClick={handleSend}
              style={{ backgroundColor: PRIMARY }}
            >
              {sending ? (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                >
                  <CircularProgress size={14} color="inherit" />
                  Sending…
                </Box>
              ) : (
                "Send feedback"
              )}
            </button>
          </Box>
        </Box>

        {history.length > 0 ? (
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${BORDER}` }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: MUTED,
                textTransform: "uppercase",
                mb: 1.25,
              }}
            >
              Feedback sent
            </Typography>
            <Box sx={{ display: "grid", gap: 1 }}>
              {history.map((entry, index) => (
                <Box
                  key={`${entry?.sentAt || ""}-${index}`}
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    border: `1px solid ${BORDER}`,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}
                    >
                      {entry?.sentAt
                        ? moment(entry.sentAt).format("DD MMM YYYY h:mm A")
                        : "—"}
                    </Typography>
                    {entry?.sentByName ? (
                      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                        · {entry.sentByName}
                      </Typography>
                    ) : null}
                    {entryChannels(entry).map((channel) => (
                      <Tooltip
                        key={channel.key}
                        title={
                          channel.failed
                            ? channel.error || CHANNELS[channel.key].failed
                            : ""
                        }
                      >
                        <Chip
                          size="small"
                          label={CHANNELS[channel.key].label}
                          color={channel.failed ? "warning" : "default"}
                          variant={channel.failed ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  {looksLikeHtml(entry?.message) ? (
                    <Box
                      sx={{
                        fontSize: 13.5,
                        color: "#0f172a",
                        wordBreak: "break-word",
                        "& p": { m: 0, mb: 0.75 },
                        "& p:last-child": { mb: 0 },
                        "& img": { maxWidth: "100%" },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: entry?.message || "",
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        color: "#0f172a",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {entry?.message || ""}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
}
